import { supabase } from './supabase'

// Hardcoded so it works even if VITE_VAPID_PUBLIC_KEY is missing from the
// build environment. Must match the private key in send-push edge function.
const VAPID_PUBLIC_KEY = 'BCupTVG144edF2fIN_fo1sZKIXvsJFkzk99z88EU0MKTjUyF9uihs5GwrSZIOuPse2XJsbzaxgsUo0TCQ-vVcbM'

// Prevent concurrent subscribe calls (e.g. useOneSignalSync + Feed both fire on mount)
let _subscribing = false

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)))
}

/**
 * Subscribe the current browser to Web Push and save the subscription to
 * users.push_subscription (the column that send-push edge function reads).
 *
 * Call this ONLY when Notification.permission === 'granted' — i.e. from a
 * user-gesture handler or after requestPermission() succeeds.
 *
 * Dead-token handling: if DB is null (cleared by a 410 response) but the
 * browser still holds the stale endpoint, we force-unsubscribe first so
 * FCM gives us a fresh, valid token.
 */
export async function subscribeToPush(userId) {
  if (_subscribing) {
    console.log('[push] subscribe already in progress, skipping')
    return null
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('[push] Web Push not supported in this browser')
    return null
  }
  if (!userId) {
    console.warn('[push] no userId provided')
    return null
  }
  if (Notification.permission !== 'granted') {
    console.warn('[push] permission not granted:', Notification.permission)
    return null
  }

  _subscribing = true
  try {
    const reg = await navigator.serviceWorker.ready
    const appKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY)

    // Check DB: if null, the last token was invalidated by a 410 from FCM.
    // Force-unsubscribe the browser's stale token so we get a fresh one.
    const { data: userData } = await supabase
      .from('users')
      .select('push_subscription')
      .eq('id', userId)
      .single()

    const existing = await reg.pushManager.getSubscription()

    if (existing && !userData?.push_subscription) {
      console.log('[push] DB null with stale browser sub — force unsubscribe for fresh token')
      await existing.unsubscribe()
    }

    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appKey,
    })

    // Write to users.push_subscription — this is what send-push reads.
    const { error } = await supabase
      .from('users')
      .update({ push_subscription: sub.toJSON() })
      .eq('id', userId)

    if (error) {
      console.error('[push] failed to save subscription to DB:', error)
      return null
    }

    console.log('[push] subscription saved for user', userId)
    return sub
  } catch (err) {
    console.error('[push] subscribe failed:', err)
    return null
  } finally {
    _subscribing = false
  }
}